import {
  Box,
  ButtonGroup,
  Center,
  Flex,
  IconButton,
  Image as ChakraImage,
  Link,
  Spacer,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { DeleteIcon, DuplicateIcon, PencilIcon } from '@diamond/shared/icons';
import { PersonalizedBannerItem } from '@diamond/shared/types';
import { Heading, Modal, Text } from '@diamond/shared/ui';
import { LIST_FEATURE_FLAGS, useFeatureFlag } from '@diamond/shared/utils';
import React from 'react';

interface BannerItemProps {
  item: PersonalizedBannerItem;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onDuplicate?: (id: string) => void;
}

function BannerItemComponent({
  item,
  onDelete,
  onEdit,
  onDuplicate,
}: BannerItemProps) {
  const isNewCg3 = useFeatureFlag(LIST_FEATURE_FLAGS.FF_BANNER_CG3_NEW, false);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const typeLabelMap: Record<string, string> = {
    specific: 'Spesifik',
    general: 'Umum',
  };
  const typeLabel =
    typeLabelMap[item?.banner_type ?? ''] ?? item?.banner_type ?? 'Umum';

  const metaParts: string[] = [];
  if (item?.business_segmentation?.length)
    metaParts.push(`${item.business_segmentation.length} Tipe cust`);
  else metaParts.push('Semua Tipe cust');

  if (item?.distribution_channel_count && item.distribution_channel_count > 0)
    metaParts.push(`${item.distribution_channel_count} Cabang`);
  else metaParts.push('Semua Cabang');

  if (item?.shipto_count && item.shipto_count > 0)
    metaParts.push(`${item.shipto_count} Shipto`);
  const metaText = metaParts.join(' • ');

  const handleEdit = () => onEdit(item.id);
  const handleDuplicate = () => onDuplicate?.(item.id);
  const handleDeleteClick = () => onDeleteOpen();

  return (
    <Flex alignItems="center" gap={4} px={2} maxW="container.xl">
      <Box rounded="4xl" w={{ base: '77px', md: '307px' }}>
        <ChakraImage
          src={item.image}
          h={{ base: '32px', md: '106px' }}
          w={{ base: '77px', md: '307px' }}
          rounded="md"
          alt="logo"
        />
      </Box>
      <Spacer />
      <Flex alignItems="center" gap="4" w="full" maxW="full">
        <Box flexBasis={240}>
          <Text fontWeight="medium">{item.title}</Text>
        </Box>
        <Box flex="1">
          <Link
            href={item?.url as string}
            isExternal
            wordBreak="break-all"
            color="blue.500"
          >
            {item?.url}
          </Link>
        </Box>
        <Box flexBasis={200}>
          <Stack spacing={1}>
            <Text
              fontSize="14px"
              fontWeight="bold"
              lineHeight="100%"
              color="black"
            >
              {typeLabel}
            </Text>
            <Text
              fontSize="14px"
              fontWeight="400"
              lineHeight="100%"
              color="black"
            >
              {metaText}
            </Text>
          </Stack>
        </Box>
      </Flex>
      <Spacer />
      <ButtonGroup>
        <IconButton
          aria-label="Edit"
          icon={<PencilIcon />}
          fontSize="xs"
          variant="solid"
          borderColor="gray.33"
          background="#F2F2F7"
          onClick={handleEdit}
        />
        {isNewCg3 && (
          <IconButton
            aria-label="Duplicate"
            icon={<DuplicateIcon />}
            fontSize="xs"
            variant="solid"
            borderColor="gray.33"
            background="#F2F2F7"
            onClick={handleDuplicate}
          />
        )}

        <IconButton
          aria-label="Delete"
          icon={<DeleteIcon />}
          fontSize="xs"
          variant="solid"
          borderColor="gray.33"
          background="#F2F2F7"
          color="white"
          onClick={handleDeleteClick}
        />
      </ButtonGroup>

      <Modal
        name="delete-banner"
        labelSubmit="Hapus"
        labelCancel="Kembali"
        buttonColor="red"
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onSubmit={() => onDelete(item.id)}
        isCentered
      >
        <Center py={4}>
          <Heading as="h3">Hapus Banner?</Heading>
        </Center>
      </Modal>
    </Flex>
  );
}

export const BannerItem = React.memo(BannerItemComponent);
